import { create } from "zustand";

import type { LonLat } from "./corridorFrame";
import { type PlacedTag, stackTags, type TagObstacle, type TagStroke, tagWidthPx } from "./zoMeasures";

/** Puces des LIMA sans recouvrement (règle d'Alexis : chaque LIMA dépasse visiblement les deux limites, sa puce
 *  au-delà de son bout ; deux puces ne se recouvrent jamais) et géométrie du calque des LIMA telle qu'elle est
 *  affichée, source unique pour les lignes de la ZO (obstacles et traits de leurs puces, limites de leur
 *  dépassement).
 *
 *  - Placement : `stackTags`, dans l'ordre des LIMA (L0 garde sa place) ; une puce qui recouvre une puce déjà posée
 *    (ou une marque d'échelon) glisse vers l'extérieur dans l'axe de sa ligne, au-delà du bout, du minimum qui la
 *    dégage plus 4 px ; elle avance aussi tant qu'elle touche une limite du fuseau ou une autre LIMA. La ligne garde
 *    son dépassement.
 *  - Recalcul : au quart de niveau de zoom (`overshootZoom`, comme le dépassement) et au cap de la carte, sur la
 *    projection de Mercator de la carte (`mercatorProjector`), sans lire la carte : les écarts réels, au zoom
 *    courant (jamais en dessous du quart arrondi), ne sont que plus grands. Le point d'ancrage rendu est
 *    géographique : la carte, à son zoom réel, et le calque des lignes de la ZO le projettent tel quel. */

/** Projection de Mercator de la carte (tuiles de 512 px) au zoom `zoom`, carte tournée du cap `bearing` (°) : pixels
 *  à une translation près, comme `map.project` sans inclinaison. */
export function mercatorProjector(zoom: number, bearing = 0): (p: LonLat) => readonly [number, number] {
  const world = 512 * 2 ** zoom;
  const rad = (bearing * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return (p) => {
    const x = ((p[0] + 180) / 360) * world;
    const lat = (Math.min(85.05, Math.max(-85.05, p[1])) * Math.PI) / 180;
    const y = ((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * world;
    // Carte au cap `bearing` : ce cap pointe vers le haut de l'écran.
    return [x * cos + y * sin, -x * sin + y * cos];
  };
}

export interface LimaChipLine {
  name: string;
  /** Tracé dessiné (prolongé selon le zoom), fini à l'extrémité qui porte la puce. */
  coords: readonly LonLat[];
  /** Nom affiché (L0, ou PL0 pour un dossier en anglais) et GDH : largeur estimée de la puce. */
  label: string;
  detail: string | null;
}

/** Puces des LIMA posées sans recouvrement (voir l'en-tête), au zoom `zoom` et au cap `bearing` ; `rails` : limites du
 *  fuseau dessinées, `marks` : marques d'échelon. Fonction pure. */
export function stackLimaChips(
  lines: readonly LimaChipLine[],
  o: { zoom: number; bearing?: number; rails?: readonly (readonly LonLat[])[]; marks?: readonly LonLat[] },
): PlacedTag[] {
  const strokes: TagStroke[] = [
    ...(o.rails ?? []).map((line) => ({ line })),
    ...lines.map((line) => ({ id: line.name, line: line.coords })),
  ];
  return stackTags(
    lines.map((line) => ({ id: line.name, line: line.coords, widthPx: tagWidthPx(line.label, line.detail) })),
    (o.marks ?? []).map((at) => ({ at })),
    mercatorProjector(o.zoom, o.bearing ?? 0),
    { strokes },
  );
}

/** Calque des LIMA tel qu'il est affiché (CorridorLayers le publie à chaque changement). */
export interface LimaGeometry {
  /** Puces des LIMA affichées ; vide quand le calque des LIMA est masqué. */
  chips: readonly PlacedTag[];
  /** LIMA dessinées (tracés prolongés) ; vide quand le calque est masqué. */
  lines: readonly (readonly LonLat[])[];
  /** Limites du fuseau dessinées (même masquées : elles bornent le dépassement des lignes de la ZO). */
  rails: readonly (readonly LonLat[])[];
  /** Marques d'échelon (II, X, XX) des limites. */
  marks: readonly LonLat[];
}

export const EMPTY_LIMA_GEOMETRY: LimaGeometry = Object.freeze({ chips: [], lines: [], rails: [], marks: [] });

export const useLimaGeometry = create<{ geometry: LimaGeometry; setGeometry: (geometry: LimaGeometry) => void }>()((set) => ({
  geometry: EMPTY_LIMA_GEOMETRY,
  setGeometry: (geometry) => set({ geometry }),
}));

/** Obstacles des puces des lignes de la ZO : marques d'échelon et puces des LIMA à leur place affichée (boîte au point
 *  d'ancrage, dans l'axe de leur ligne). */
export function limaObstacles(geometry: LimaGeometry): TagObstacle[] {
  return [
    ...geometry.marks.map((at) => ({ at })),
    ...geometry.chips.map((chip) => ({ line: [chip.from, chip.at], widthPx: chip.widthPx, offsetPx: chip.offsetPx })),
  ];
}
